import type { Meal } from '../../shared/meals'
import { lines, type From, type Recipe, type RecipePhoto, type RecipeSummary } from './recipes'

// The Modern Recipes page (the blend): kitchens across the top, chips and an ingredient box to narrow the wall, and a
// wall of whole dishes that each turn over to their card. Everything here is worked out from what the family already
// keeps: who a recipe is from, its meals, its book, its ingredients as typed, and its photos.

const folded = (text: string) => text.normalize('NFD').replace(/\p{M}/gu, '').toLocaleLowerCase()

// A kitchen: everyone a recipe is from, with their recipes newest first. Kitchens with the newest recipes come first.
export type Kitchen = { person: From; recipes: RecipeSummary[]; newest: number }
export function kitchens(recipes: RecipeSummary[]): Kitchen[] {
  const byPerson = new Map<string, Kitchen>()
  for (const recipe of recipes) {
    if (!recipe.from) continue
    const kitchen = byPerson.get(recipe.from.id) ?? { person: recipe.from, recipes: [], newest: 0 }
    kitchen.recipes.push(recipe)
    kitchen.newest = Math.max(kitchen.newest, recipe.createdAt)
    byPerson.set(recipe.from.id, kitchen)
  }
  for (const kitchen of byPerson.values()) kitchen.recipes.sort((a, b) => b.createdAt - a.createdAt)
  return [...byPerson.values()].sort(
    (a, b) => b.newest - a.newest || a.person.name.localeCompare(b.person.name, undefined, { sensitivity: 'base' }),
  )
}

// What narrows the wall: one chip (a meal, a person a recipe is from, or a book) and what's typed in the ingredient box.
export type Chip = { kind: 'meal'; meal: Meal } | { kind: 'from'; id: string } | { kind: 'book'; id: string } | null

// The first ingredient line that has every typed word, for "Uses butter" under a tile. Typing matches the recipe's
// name too, so "flan" finds Abuela's flan even when it lists no ingredients; then nothing is underlined.
export function ingredientMatch(recipe: Pick<RecipeSummary, 'title' | 'ingredients'>, typed: string) {
  const words = folded(typed).split(/\s+/).filter(Boolean)
  if (!words.length) return { matches: true, line: null }
  const line = lines(recipe.ingredients ?? '').find((x) => words.every((w) => folded(x).includes(w))) ?? null
  if (line) return { matches: true, line }
  return { matches: words.every((w) => folded(recipe.title).includes(w)), line: null }
}

export function narrow(recipes: RecipeSummary[], chip: Chip, typed: string) {
  return recipes.filter((x) => {
    if (chip?.kind === 'meal' && !x.meals.includes(chip.meal)) return false
    if (chip?.kind === 'from' && x.from?.id !== chip.id) return false
    if (chip?.kind === 'book' && x.book?.id !== chip.id) return false
    return ingredientMatch(x, typed).matches
  })
}

// The typed words' places in a line, for underlining them: [start, end) pairs in order, not overlapping. Matching
// ignores case and accents; folding can change a string's length only for rare letters, so positions are found on the
// folded line and kept when the lengths agree.
export function marks(line: string, typed: string): [number, number][] {
  const text = folded(line)
  if (text.length !== line.length) return []
  const found: [number, number][] = []
  for (const word of folded(typed).split(/\s+/).filter(Boolean)) {
    const at = text.indexOf(word)
    if (at >= 0) found.push([at, at + word.length])
  }
  found.sort((a, b) => a[0] - b[0] || b[1] - a[1])
  return found.filter((m, i) => i === 0 || m[0] >= found[i - 1]![1])
}

// Whether a tile can turn over to its card: it has a photo of the card, and the tile isn't already showing it.
export const turnable = (recipe: Pick<RecipeSummary, 'tile' | 'card'>) =>
  !!recipe.card && recipe.tile?.ref !== recipe.card.ref

// How tall a tile's picture is for its width: its photo's own shape, held between a tall portrait and a wide
// landscape so a panorama doesn't become a sliver. A recipe with no photo gets a square of its name.
export const tileRatio = (recipe: Pick<RecipeSummary, 'tile'>) =>
  recipe.tile ? Math.min(1.6, Math.max(0.6, recipe.tile.height / recipe.tile.width)) : 1

// The wall's columns, filled in order, each tile going to the shortest column so far: the newest stay near the top
// and reading runs roughly left to right. The extra is the height of a tile's words, in widths.
export function columns<T>(items: T[], count: number, ratio: (item: T) => number, extra = 0.28): T[][] {
  const cols: T[][] = Array.from({ length: Math.max(1, count) }, () => [])
  const heights = cols.map(() => 0)
  for (const item of items) {
    const i = heights.indexOf(Math.min(...heights))
    cols[i]!.push(item)
    heights[i]! += ratio(item) + extra
  }
  return cols
}

// A recipe post's photos in the order they're swiped: the dish (the one on its tile first), then the card, then the
// dish as others in the family made it, newest first.
export type Slide = { kind: 'food' | 'card' | 'made'; photo: RecipePhoto }
export function postSlides(recipe: Pick<Recipe, 'photos' | 'hero'>): Slide[] {
  const of = (kind: Slide['kind']) => recipe.photos.filter((p) => p.kind === kind)
  const food = of('food').sort((a, b) => Number(b.id === recipe.hero) - Number(a.id === recipe.hero))
  const made = of('made').sort((a, b) => (b.addedAt ?? 0) - (a.addedAt ?? 0))
  // A picked card photo on the tile leads too, so the post opens on what the wall showed.
  const cards = of('card').sort((a, b) => Number(b.id === recipe.hero) - Number(a.id === recipe.hero))
  const heroIsCard = cards[0]?.id === recipe.hero
  const lead = heroIsCard ? [...cards.map((photo) => ({ kind: 'card' as const, photo }))] : []
  return [
    ...lead,
    ...food.map((photo) => ({ kind: 'food' as const, photo })),
    ...(heroIsCard ? [] : cards.map((photo) => ({ kind: 'card' as const, photo }))),
    ...made.map((photo) => ({ kind: 'made' as const, photo })),
  ]
}
